import { FC, useEffect, useMemo } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Autocomplete, Box, InputAdornment, MenuItem, Stack, TextField } from '@mui/material'

import { UserLanguage } from '../../../../../features/account/types/User'
import { InputTranslator } from '../../../../components/InputTranslator/InputTranslator'
import { InternalFormDialog } from '../../../../components/InternalFormDialog/InternalFormDialog'
import { InternalTag } from '../../../../types/InternalTag'
import {
  getTagOptions,
  isValidColorHex,
  tagGroupTypeLabelKeys,
  tagGroupTypes,
  TagGroupFormValues,
  TagGroupRow,
  toTagGroupFormValues,
  toTagGroupMutation,
} from '../../helpers/tagGroupHelpers'
import { TagGroupMutation, TagGroupType } from '../../types/InternalTagGroup'

const tagGroupLanguages: UserLanguage[] = ['en', 'ja', 'zh']

type TagGroupEditDialogProps = {
  open: boolean
  row?: TagGroupRow
  defaultType: TagGroupType
  tags: InternalTag[]
  isSaving?: boolean
  onClose: () => void
  onSave: (tagGroup: TagGroupMutation) => void
}

// No <form> here: the InputTranslator dialog has its own form, and React bubbles its submit through the portal into any parent form.
export const TagGroupEditDialog: FC<TagGroupEditDialogProps> = ({ open, row, defaultType, tags, isSaving, onClose, onSave }) => {
  const { t } = useTranslation()
  const { control, handleSubmit, reset, formState, watch, setValue } = useForm<TagGroupFormValues>({
    mode: 'onChange',
    defaultValues: toTagGroupFormValues(row, defaultType),
  })

  useEffect(() => {
    if (open) {
      reset(toTagGroupFormValues(row, defaultType))
    }
  }, [open, row, defaultType, reset])

  const type = watch('type')
  const colorHex = watch('colorHex')
  const tagOptions = useMemo(() => getTagOptions(tags, type), [tags, type])
  const tagsById = useMemo(() => new Map(tags.map((tag) => [tag.id, tag])), [tags])

  const submit = handleSubmit((values) => onSave(toTagGroupMutation(row?.tagGroup, values)))

  return (
    <InternalFormDialog
      open={open}
      title={row ? t('internal-service-management:tag_groups_edit') : t('internal-service-management:tag_groups_new')}
      onClose={onClose}
      onSave={submit}
      saveDisabled={isSaving || !formState.isValid}
    >
      <Stack spacing={2} pt={1}>
        <Controller
          name="names"
          control={control}
          rules={{ validate: (names) => !!names.en?.trim() }}
          render={({ field }) => (
            <InputTranslator
              value={field.value}
              onChange={field.onChange}
              label={t('internal-service-management:tag_groups_name')}
              dialogTitle={t('internal-service-management:tag_groups_name')}
              selectedLanguages={tagGroupLanguages}
            />
          )}
        />
        <Stack direction="row" spacing={2}>
          <Controller
            name="colorHex"
            control={control}
            rules={{ validate: (value) => isValidColorHex(value) || t('internal-service-management:tag_groups_color_invalid') }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                size="small"
                label={t('internal-service-management:tag_groups_color')}
                placeholder="#FF00FF"
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                sx={{ flex: 1 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Box
                        sx={{
                          width: 16,
                          height: 16,
                          border: 1,
                          borderColor: 'divider',
                          bgcolor: isValidColorHex(colorHex) && colorHex.trim() ? colorHex.trim() : 'transparent',
                        }}
                      />
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />
          <Controller
            name="type"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                select
                size="small"
                label={t('internal-service-management:tag_groups_type')}
                sx={{ flex: 1 }}
                onChange={(event) => {
                  field.onChange(event.target.value)
                  setValue('tagIds', [], { shouldDirty: true })
                }}
              >
                {tagGroupTypes.map((tagGroupType) => (
                  <MenuItem key={tagGroupType} value={tagGroupType}>
                    {t(tagGroupTypeLabelKeys[tagGroupType])}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </Stack>
        <Controller
          name="tagIds"
          control={control}
          render={({ field }) => (
            <Autocomplete
              multiple
              filterSelectedOptions
              options={tagOptions}
              value={field.value.map((tagId) => tagsById.get(tagId) ?? { id: tagId, name: tagId, type })}
              onChange={(_, value) => field.onChange(value.map((tag) => tag.id))}
              getOptionLabel={(tag) => tag.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => <TextField {...params} label={t('internal-service-management:tag_groups_tags')} />}
            />
          )}
        />
      </Stack>
    </InternalFormDialog>
  )
}
